import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import ToastBody from "@/components/ui/toast-body";
import CreateLeadModal from "@/pages/leads/components/CreateLeadModal";
import CreateDealModal from "@/pages/deals/components/CreateDealModal";
import CreateContractModal from "@/pages/contracts/components/CreateContractModal";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import { PersonData, PersonStatus } from "@/entities/people/types";
import { PersonQualificationModalState } from "@/components/layouts/PersonDetailedView";
import { getFullName } from "@/entities/people/helpers";
import PersonQualificationModal from "@/components/layouts/PersonQualificationModal";
import { useResetFilteredDeals } from "@/pages/deals/hooks";
import { useResetLeadsInfinite } from "@/pages/leads/hooks";
import { isNull } from "lodash";

export const usePersonActions = (
  personEntity: PersonData | undefined,
  userData?: any,
  isTabView?: boolean,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCopyLink = useCopyLink();

  const {
    person,
    setPerson,
    resetPeople,
    onUpdatePersonField: onUpdateField,
    deletePerson
  } = useEntityType(
    EntityType.PERSON,
    personEntity,
    false,
    undefined,
    undefined,
    forceRefetch,
    includeAdditionals
  );

  const updatePersonState = useCallback(
    (updatedFields: Partial<PersonData>) => {
      if (person) {
        setPerson({
          ...person,
          ...updatedFields
        });
      }
    },
    [person, setPerson]
  );

  const personFullName = person ? getFullName(person) : "";

  const resetFilteredDeals = useResetFilteredDeals();
  const resetLeadsInfinite = useResetLeadsInfinite();

  const [isOpenDeletePersonModal, setIsOpenDeletePersonModal] = useState(false);
  const [personQualificationModalState, setPersonQualificationModalState] =
    useState<PersonQualificationModalState>({
      isOpen: false,
      mode: "disqualify",
      initialReason: ""
    });
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false);
  const [isCreateContractModalOpen, setIsCreateContractModalOpen] =
    useState(false);

  const toggleTasksDrawer = () => setIsTasksDrawerOpen(!isTasksDrawerOpen);


  const handleDelete = (
    setIsOpenDeletePersonModal: Dispatch<SetStateAction<boolean>>,
    isQuickView: boolean = false,
    closeDrawer?: () => void
  ) => {
    if (person) {
      deletePerson.mutate(
        {
          pathParams: { personId: person.id }
        },
        {
          onSuccess: () => {
            if (isQuickView) {
              setIsOpenDeletePersonModal(false);
              closeDrawer?.();
            } else {
              navigate("/people", { replace: true });
            }
            resetPeople();
            toast.success(
              <ToastBody title={`${t("people.personView.toast1")} ${personFullName} ${t("people.personView.toast2")}`} />,
              {
                duration: 5000,
                icon: null
              }
            );
          }
        }
      );
    }
  };

  const handleRequalifyPerson = () => {
    setPersonQualificationModalState({
      isOpen: true,
      mode: "requalify",
      initialReason: ""
    });
  };

  const handleDisqualifyPerson = () => {
    setPersonQualificationModalState({
      isOpen: true,
      mode: "disqualify",
      initialReason: ""
    });
  };

  const handleEditDisqualification = () => {
    setPersonQualificationModalState({
      isOpen: true,
      mode: "edit",
      initialReason:
        person?.statusReasonText && !isNull(person?.statusReasonText)
          ? person?.statusReasonText
          : ""
    });
  };

  const getCreateOptions = () => {
    if (!userData) return {};
    return {
      lead: () => setIsCreateLeadModalOpen(true),
      deal: () => setIsCreateDealModalOpen(true),
      contract: () => setIsCreateContractModalOpen(true)
    } as DetailedViewOptions;
  };

  const onCreateLead = () => setIsCreateLeadModalOpen(true);
  const onCreateDeal = () => setIsCreateDealModalOpen(true);
  const onCreateContract = () => setIsCreateContractModalOpen(true);

  const handleCreateSuccess = () => {
    // Reset deals and leads data to refresh the InvolvedIn section
    resetFilteredDeals();
    resetLeadsInfinite();
  };

  const getTopOptions = () => {
    if (person?.status === PersonStatus.DISQUALIFIED) {
      return {
        requalifyPerson: () => handleRequalifyPerson()
      } as DetailedViewOptions;
    }
    return {
      disqualifyPerson: () => handleDisqualifyPerson()
    } as DetailedViewOptions;
  };

  const getOptions = (isQuickView: boolean = false, personId?: string) => {
    return {
      copyLink: () =>
        handleCopyLink(
          isQuickView,
          personId,
          isTabView ? "/people" : undefined
        ),
      personLog: () =>
        isQuickView
          ? navigate(`/people/${personId}/log`, { replace: true })
          : navigate("log"),
      delete: {
        action: () => setIsOpenDeletePersonModal(true),
        variant: "destructive"
      }
    } as DetailedViewOptions;
  };

  const setIsPersonQualificationModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    setPersonQualificationModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  const renderModals = (
    isDropdownClosing = false,
    isQuickView = false,
    onClose?: () => void,
    userData?: any
  ) => (
    <>
      <DeleteEntityModal
        isOpen={isOpenDeletePersonModal && !isDropdownClosing}
        setIsOpen={setIsOpenDeletePersonModal}
        entity={EntityType.PERSON}
        entityName={personFullName}
        handleDelete={() =>
          handleDelete(setIsOpenDeletePersonModal, isQuickView, onClose)
        }
      />
      <PersonQualificationModal
        person={person}
        isOpen={personQualificationModalState.isOpen && !isDropdownClosing}
        setIsOpen={setIsPersonQualificationModalOpen}
        mode={personQualificationModalState.mode}
        initialReason={personQualificationModalState.initialReason}
        onUpdatePerson={(newPersonData: PersonData) => {
          setPerson(newPersonData);
        }}
      />
      {userData && (
        <>
          <CreateLeadModal
            isOpen={isCreateLeadModalOpen && !isDropdownClosing}
            setIsOpen={setIsCreateLeadModalOpen}
            userData={userData}
            resetPage={handleCreateSuccess}
            companyId={person?.companyId}
            personId={person?.id}
          />
          <CreateDealModal
            isOpen={isCreateDealModalOpen && !isDropdownClosing}
            setIsOpen={setIsCreateDealModalOpen}
            userData={userData}
            onDealCreated={handleCreateSuccess}
            companyId={person?.companyId}
            personId={person?.id}
          />
          <CreateContractModal
            isOpen={isCreateContractModalOpen && !isDropdownClosing}
            setIsOpen={setIsCreateContractModalOpen}
            userData={userData}
            onContractCreated={handleCreateSuccess}
            companyId={person?.companyId}
            personId={person?.id}
          />
        </>
      )}
    </>
  );

  return {
    person,
    updatePersonState,
    personFullName,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getTopOptions,
    getCreateOptions,
    getOptions,
    renderModals,
    handleEditDisqualification,
    onCreateLead,
    onCreateDeal,
    onCreateContract
  };
};
